import { Injectable, OnDestroy } from '@angular/core'
import {
  RootState,
  SavedView,
  selectActiveWorkspace,
  selectUpdatedSavedView,
  setActiveSavedViews,
  Workspace,
} from '@helios/state-management'
import { Store } from '@ngrx/store'
import { asyncScheduler, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  skip,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs/operators'
import { WorkspaceService } from './workspace.service'

@Injectable({
  providedIn: 'root',
})
export class WorkspaceUpdateListenerService implements OnDestroy {
  // workspaces will change when changing focused panel
  // We saw high load during load testing.  These updates are not important to be immediate, but we update when workspace changes to prevent loss
  static readonly ACTIVE_WORKSPACE_THROTTLE_TIME = 1 * 1000
  // Saved views update much less frequently, and would be more complicated to update on workspace change
  static readonly SAVED_VIEW_THROTTLE_TIME = 2 * 1000

  private readonly _destroySubject$ = new Subject<void>()
  private currentWorkspace: Workspace | undefined

  constructor(
    private readonly store: Store<RootState>,
    private readonly service: WorkspaceService,
  ) {}

  ngOnDestroy() {
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  /**
   * Listens for Workspace and Saved View updates and propagates those updates to the backend. In addition,
   * on Workspace change it will pull the respective saved views into the ngrx store.
   *
   */
  listen() {
    this.keepActiveWorkspaceUpdated().subscribe()
    this.updateOldWorkspaceWhenSwitched().subscribe()
    this.loadSavedViewsOnNewActiveWorkspace().subscribe()
    this.keepActiveSavedViewsUpdated().subscribe()
  }

  /**
   * @internal
   */
  keepActiveWorkspaceUpdated() {
    return this.store.select(selectActiveWorkspace).pipe(
      skip(1),
      takeUntil(this._destroySubject$),
      filter((workspace): workspace is Workspace => workspace != null),
      throttleTime(
        WorkspaceUpdateListenerService.ACTIVE_WORKSPACE_THROTTLE_TIME,
        asyncScheduler,
        {
          leading: false,
          trailing: true,
        },
      ),
      tap(workspace => {
        //this will update the workspace one minute after the last change, but may miss a recent change to a prior workspace - thus updateOldWorkspaceWhenSwitched
        this.service.updateWorkspace(workspace.workspacePrefix, {
          workspace: workspace.workspace,
        })
      }),
    )
  }

  /**
   * Update workspace with server when we switch workspaces.
   * This is needed because we heavily debounce workspace updates, and may have debounced the latest update before switching
   *
   * @internal
   */
  updateOldWorkspaceWhenSwitched() {
    return this.store.select(selectActiveWorkspace).pipe(
      takeUntil(this._destroySubject$),
      tap(workspace => {
        const oldWorkspace = this.currentWorkspace
        this.currentWorkspace = workspace as Workspace
        if (oldWorkspace === undefined) return
        if (oldWorkspace.workspacePrefix === workspace?.workspacePrefix) return //same workspace, do not update - that will be debounced in keepActiveWorkspaceUpdated
        //update the old workspace - we just switched to a different one
        this.service.updateWorkspace(oldWorkspace.workspacePrefix, {
          workspace: oldWorkspace.workspace,
        })
      }),
    )
  }

  /**
   * @internal
   */
  loadSavedViewsOnNewActiveWorkspace() {
    return this.store.select(selectActiveWorkspace).pipe(
      takeUntil(this._destroySubject$),
      filter((workspace): workspace is Workspace => workspace != null),
      distinctUntilChanged((x, y) => x.workspacePrefix === y.workspacePrefix),
      switchMap(workspace =>
        this.service.getSavedViews(workspace.workspacePrefix),
      ),
      tap(savedViews => this.store.dispatch(setActiveSavedViews(savedViews))),
    )
  }

  /**
   * @internal
   */
  keepActiveSavedViewsUpdated() {
    return this.store.select(selectUpdatedSavedView).pipe(
      takeUntil(this._destroySubject$),
      filter((view): view is SavedView => view != null),
      throttleTime(
        WorkspaceUpdateListenerService.SAVED_VIEW_THROTTLE_TIME,
        asyncScheduler,
        {
          leading: false,
          trailing: true,
        },
      ),
      tap(view => {
        this.service.updateSavedView(view.savedViewPrefix, {
          savedView: view.savedView,
        })
      }),
    )
  }
}
